import { ref, computed } from 'vue';
import { api } from '@/services/api';

/**
 * BRICK Schema Visualization Composable
 *
 * Handles TTL generation, BRICK relationship management,
 * and schema visualization for equipment documentation.
 *
 * @param {Object} options
 * @param {number} options.equipmentId - Equipment ID
 * @param {number} options.siteId - Site ID
 * @param {string} options.equipmentName - Equipment name
 * @param {string} options.brickClass - Equipment BRICK class
 * @param {Object} options.documentation - Documentation data ref
 * @param {Object} options.equipmentDetails - Equipment details ref
 */
export function useBrickSchemaVisualization({
  equipmentId,
  siteId,
  equipmentName,
  brickClass,
  documentation,
  equipmentDetails,
}) {
  const ttlPreview = ref('');
  const generating = ref(false);

  /**
   * Helper function to sanitize names for BRICK URIs
   */
  function sanitizeNameForUri(name) {
    if (!name) {return '';}
    return name
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  }

  /**
   * Helper function to clean BRICK class names (remove brick: prefix if present)
   */
  function cleanBrickClass(brickClassValue) {
    if (!brickClassValue) {return null;}
    if (brickClassValue.startsWith('brick:')) {
      return brickClassValue.replace('brick:', '');
    }
    return brickClassValue;
  }

  /**
   * Create equipment URI
   */
  function createEquipmentUri(name = null, id = null) {
    const equipName = name || equipmentName || `Equipment_${equipmentId}`;
    const sanitized = sanitizeNameForUri(equipName);
    return id ? `${sanitized}_${id}` : sanitized;
  }

  /**
   * Create zone URI
   */
  function createZoneUri(zoneName, zoneId = null) {
    const equipName = sanitizeNameForUri(equipmentName || `Equipment_${equipmentId}`);
    const zoneNameSanitized = sanitizeNameForUri(zoneName || `Zone_${zoneId}`);
    return `${equipName}_${zoneNameSanitized}`;
  }

  /**
   * Fetch unit mapping by ID
   */
  async function getUnitMapping(unitId) {
    try {
      const response = await api.get(`/v2/unit-mappings/${unitId}`);
      return response;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate TTL preview for BRICK schema
   *
   * NOTE: This is a simplified version. The full implementation requires:
   * - Building/level context
   * - Zone sections with feeds relationships
   * - Device hierarchies with compound components
   * - Point type mappings
   * - Space relationships
   *
   * For complete functionality, see DocumentationDialog.vue:generateTtlPreview()
   */
  async function generateTtlPreview() {
    if (!documentation?.value) {
      return '# No documentation data available';
    }

    generating.value = true;
    try {
      const equipmentUri = `equipment:${createEquipmentUri()}`;
      const brickClassName = cleanBrickClass(
        documentation.value.brick_class || brickClass,
      ) || 'Air_Handling_Unit';

      let ttl = `# ${equipmentName} - BRICK Schema\n`;
      ttl += '@prefix brick: <https://brickschema.org/schema/Brick#> .\n';
      ttl += '@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .\n';
      ttl += '@prefix equipment: <urn:site:equipment:> .\n';
      ttl += '@prefix zone: <urn:site:zone:> .\n';
      ttl += '@prefix device: <urn:site:device:> .\n';
      ttl += '@prefix point: <urn:site:point:> .\n';
      ttl += '@prefix space: <urn:site:space:> .\n\n';

      // Main equipment
      ttl += `${equipmentUri} a brick:${brickClassName} ;\n`;
      ttl += `    rdfs:label "${equipmentName}" ;\n`;

      // Add feeds relationships
      if (equipmentDetails?.value?.feeds_equipment?.length > 0) {
        equipmentDetails.value.feeds_equipment.forEach((fed) => {
          let fedUri;
          if (fed.brick_class?.includes('Zone')) {
            fedUri = `equipment:${createZoneUri(fed.name, fed.id)}`;
          } else {
            fedUri = `equipment:${createEquipmentUri(fed.name, fed.id)}`;
          }
          ttl += `    brick:feeds ${fedUri} ;\n`;
        });
      }

      // Add zone sections
      if (equipmentDetails?.value?.zone_sections?.length > 0) {
        equipmentDetails.value.zone_sections.forEach((zone) => {
          const zoneUri = `equipment:${createZoneUri(zone.name, zone.id)}`;
          ttl += `    brick:hasPart ${zoneUri} ;\n`;
        });
      }

      // Add devices
      if (equipmentDetails?.value?.devices?.length > 0) {
        const topLevelDevices = equipmentDetails.value.devices.filter(
          (d) => !d.parent_compound_id,
        );
        topLevelDevices.forEach((device) => {
          const deviceType = device.name || device.brick_class || 'Device';
          const deviceUri = `device:${sanitizeNameForUri(equipmentName)}_${sanitizeNameForUri(deviceType)}`;
          ttl += `    brick:hasPart ${deviceUri} ;\n`;
        });
      }

      // Add custom BRICK relationships
      if (documentation.value.brick_relationships?.length) {
        documentation.value.brick_relationships.forEach((rel) => {
          ttl += `    ${rel.predicate} ${rel.object} ;\n`;
        });
      }

      // Add technical specs
      if (documentation.value.technical_specs?.manufacturer) {
        ttl += `    brick:manufacturer "${documentation.value.technical_specs.manufacturer}" ;\n`;
      }

      if (documentation.value.technical_specs?.model) {
        ttl += `    brick:modelNumber "${documentation.value.technical_specs.model}" ;\n`;
      }

      if (documentation.value.technical_specs?.serial_number) {
        ttl += `    brick:serialNumber "${documentation.value.technical_specs.serial_number}" ;\n`;
      }

      if (documentation.value.technical_specs?.capacity) {
        ttl += `    brick:hasCapacity "${documentation.value.technical_specs.capacity}"^^xsd:float ;\n`;

        if (documentation.value.technical_specs?.capacity_unit_id) {
          const unitMapping = await getUnitMapping(
            documentation.value.technical_specs.capacity_unit_id,
          );
          if (unitMapping?.qudt_uri) {
            ttl += `    brick:hasCapacityUnit unit:${unitMapping.qudt_uri} ;\n`;
          }
        }
      }

      if (documentation.value.technical_specs?.install_date) {
        ttl += `    brick:installDate "${documentation.value.technical_specs.install_date}"^^xsd:date ;\n`;
      }

      if (documentation.value.technical_specs?.warranty_expiry) {
        ttl += `    brick:warrantyExpiry "${documentation.value.technical_specs.warranty_expiry}"^^xsd:date ;\n`;
      }

      ttl += '    .\n\n';

      // Add note about simplified version
      ttl += '# NOTE: This is a simplified TTL preview.\n';
      ttl += '# Full implementation includes devices, points, and space relationships.\n';

      return ttl;
    } catch (error) {
      return '# Error generating TTL preview';
    } finally {
      generating.value = false;
    }
  }

  /**
   * Update TTL preview
   */
  async function updateTtlPreview() {
    try {
      ttlPreview.value = await generateTtlPreview();
    } catch (error) {
      ttlPreview.value = '# Error generating TTL preview';
    }
  }

  /**
   * Get icon for BRICK relationship predicate
   */
  function getRelationshipIcon(predicate) {
    const iconMap = {
      'brick:feeds': 'arrow_forward',
      'brick:isFedBy': 'arrow_back',
      'brick:hasPoint': 'sensors',
      'brick:isPartOf': 'account_tree',
      'brick:hasLocation': 'location_on',
      'brick:controls': 'settings_remote',
      'brick:isControlledBy': 'settings',
      'brick:meters': 'speed',
      'brick:isMeteredBy': 'analytics',
    };
    return iconMap[predicate] || 'link';
  }

  /**
   * Validate BRICK class
   */
  const isValidBrickClass = computed(() => {
    if (!documentation?.value?.brick_uri) {return false;}
    return documentation.value.brick_uri.startsWith('brick:');
  });

  return {
    // State
    ttlPreview,
    generating,
    isValidBrickClass,

    // Methods
    generateTtlPreview,
    updateTtlPreview,
    getRelationshipIcon,
    sanitizeNameForUri,
    cleanBrickClass,
    createEquipmentUri,
    createZoneUri,
  };
}
